import {
    AgGridFilterType,
    AgGridServerFilterMap,
    BasicTableOptions,
    ClientMessagesTableOptions,
    ExternalTableFilter,
    UserActivityLogTableOptions
} from '@tassat/common-definitions';
import { CollateralHistoryTableOptions, TransactionHistoryTableOptions } from '@tassat/fred-extended-definitions';
import { createContext } from 'react';

export interface TableOptionsContextState {
    TableOptions: BasicTableOptions |
        ClientMessagesTableOptions |
        TransactionHistoryTableOptions |
        CollateralHistoryTableOptions |
        UserActivityLogTableOptions;
    TableOptionsDispatch: any;
}

const TableOptionsContext = createContext<TableOptionsContextState>({} as TableOptionsContextState);

const tableOptionsReducer = (state, action) => {
    switch (action.type) {
        case 'update':
            return {
                ...state,
                ...action.values
            };
        default:
            return state;
    }
};

const isColumnFiltered = (
    column,
    getMappedColumn,
    tableOptions,
    getMappedTableColumn = undefined
) => {
    if (!tableOptions) return false;

    const mappedColumn = getMappedColumn(column);
    const filterSet = tableOptions.FilterCondition.split(':');
    for (let i = 0; i < filterSet.length; i++) {
        const filterParts = filterSet[i].split('|');
        const mappedTableColumn = getMappedTableColumn && getMappedTableColumn(filterParts[0]);

        if (mappedTableColumn && mappedTableColumn === mappedColumn) {
            return 'active';
        }

        if (filterParts[0] === mappedColumn) {
            return 'active';
        }
    }

    return false;
};

const handleFilterChange = (
    column,
    operation,
    value,
    getMappedColumn,
    tableOptions,
    tableOptionsDispatch,
    dbColumn = undefined,
    getMappedTableColumn = undefined
) => {
    if (!operation || (operation !== AgGridFilterType.ClearFilter && !value)) return;

    const mappedColumn = getMappedColumn(column);
    const op = AgGridServerFilterMap[operation];
    const currentFilters = tableOptions.TableFilters;
    let filterExists = currentFilters.findIndex(filter => mappedColumn === filter.column);

    if (operation === AgGridFilterType.ClearFilter) {
        if (filterExists !== -1) {
            currentFilters.splice(filterExists, 1);

            // Conditional checks for non-basic tables if necessary
            if (tableOptions.clearSpecialColumns) {
                tableOptions.clearSpecialColumns(mappedColumn, currentFilters);
            }
        }

        const newConditionsAfterRemove = currentFilters.reduce((acc, curr) => {
            //support obscured search for originator Id. i.e., replace eq operation with like to search based on matching pattern in SQL.
            if (curr.column === 'originatorId' && curr.value.slice(0, 6) === '******') {
                acc.push(`${curr.column}|LIKE|${curr.value.slice(6)}`);
            // TODO Short term fix to avoid modifying existing logic
            } else if (curr.column === 'Amount') {
                const isLessOperation = curr.operation === 'LT';
                const condition = `fiatAmount|${curr.operation}|${curr.value}${isLessOperation ? ':' : '^'}assetQuantity|${curr.operation}|${curr.value}`;
                acc.push(condition);
            } else if (curr.dbColumn || dbColumn && getMappedTableColumn && curr.column === getMappedTableColumn(dbColumn)) {
                const mappedDbColumn = curr.dbColumn ?? dbColumn;
                acc.push(`${mappedDbColumn}|${curr.operation}|${curr.value}`);
            } else {
                acc.push(`${curr.column}|${curr.operation}|${curr.value}`);
            }

            return acc;
        }, []);

        const updateValues = {
            TableFilters: currentFilters,
            FilterCondition: newConditionsAfterRemove.join(':'),
            PageNo: 1,
            DefaultState: false
        };

        tableOptionsDispatch({
            type: 'update',
            values: updateValues
        });

        return;
    }

    let newFilter = [];
    // Conidional checks for non-basic tables if necessary
    if (tableOptions.createSpecialColumnFilters) {
        newFilter = tableOptions.createSpecialColumnFilters({
            column: mappedColumn,
            operation,
            value,
            dbColumn

        } as ExternalTableFilter);
    } else {
        newFilter.push({
            column: mappedColumn,
            operation: op,
            value,
            dbColumn
        });
    }

    if (filterExists === -1) {
        if (tableOptions.clearSpecialColumns) {
            tableOptions.clearSpecialColumns(mappedColumn, currentFilters);
        }
        currentFilters.push(...newFilter);
    } else {
        if (tableOptions.clearSpecialColumns) {
            // Check for special columns, then find mapped column again to remove
            tableOptions.clearSpecialColumns(mappedColumn, currentFilters);

            filterExists = currentFilters.findIndex((filter) => mappedColumn === filter.column);
            currentFilters.splice(filterExists, 1, ...newFilter);
        } else {
            currentFilters.splice(filterExists, 1, ...newFilter);
        }
    }

    const newConditions = currentFilters.reduce((acc, curr) => {
        //support obscured search for originator Id. i.e., replace eq operation with like to search based on matching pattern in SQL.
        if (curr.column === 'originatorId' && curr.value.slice(0, 6) === '******') {
            acc.push(`${curr.column}|LIKE|${curr.value.slice(6)}`);
        // TODO Short term fix to avoid modifying existing logic
        } else if (curr.column === 'Amount') {
            const isLessOperation = curr.operation === 'LT';
            const condition = `fiatAmount|${curr.operation}|${curr.value}${isLessOperation ? ':' : '^'}assetQuantity|${curr.operation}|${curr.value}`;
            acc.push(condition);
        } else if (curr.dbColumn || dbColumn && getMappedTableColumn && curr.column === getMappedTableColumn(dbColumn)) {
            const mappedDbColumn = curr.dbColumn ?? dbColumn;
            acc.push(`${mappedDbColumn}|${curr.operation}|${curr.value}`);
        } else {
            acc.push(`${curr.column}|${curr.operation}|${curr.value}`);
        }

        return acc;
    }, []);

    const updateValues = {
        TableFilters: currentFilters,
        FilterCondition: newConditions.join(':'),
        PageNo: 1,
        DefaultState: false
    };

    tableOptionsDispatch({
        type: 'update',
        values: updateValues
    });

    return;
};

const isColumnSorted = (column, getMappedColumn, tableOptions) => {
    if (!tableOptions) return 'default';
    if (tableOptions.DefaultState && !tableOptions.FilterCondition) return 'default';

    const mappedColumn = getMappedColumn(column);
    const sortSet = tableOptions.Sort.split('|');
    if (sortSet.length === 2) {
        const [ col, dir ] = sortSet;

        if (col === mappedColumn) {
            return dir as 'ASC' | 'DESC';
        }
    }

    return false;
};

const handleSortChange = (column, direction, getMappedColumn, tableOptions, tableOptionsDispatch) => {
    if (!column && (!direction || direction !== AgGridFilterType.ClearFilter)) return;

    const mappedColumn = getMappedColumn(column);
    const sortSet = tableOptions.Sort.split('|');

    if (direction === AgGridFilterType.ClearFilter && sortSet[0] !== mappedColumn) {
        return;
    }

    if (direction === AgGridFilterType.ClearFilter && sortSet[0] === mappedColumn) {
        const updateValues = {
            Sort: tableOptions.DefaultSort,
            PageNo: 1,
            DefaultState: false
        };

        tableOptionsDispatch({
            type: 'update',
            values: updateValues
        });

        return;
    }

    const updateValues = {
        Sort: `${mappedColumn}|${direction}`,
        PageNo: 1,
        DefaultState: false
    };

    tableOptionsDispatch({
        type: 'update',
        values: updateValues
    });

    return;
};

export {
    tableOptionsReducer,
    TableOptionsContext,
    isColumnFiltered,
    handleFilterChange,
    isColumnSorted,
    handleSortChange
};